// pages/TourGuidePage.jsx
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Container,
  Box,
  Typography,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Button,
  Chip,
  Skeleton,
  Paper,
  Avatar,
  IconButton,
  TextField,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Divider,
  Stack,
  Drawer,
  Slider,
  Switch,
  FormControlLabel,
  Collapse,
  Pagination,
  Snackbar,
  Alert,
  Tooltip,
  Badge,
  Rating,
  useMediaQuery,
  useTheme,
  Fade,
  Zoom,
} from '@mui/material';
import { Helmet } from 'react-helmet-async';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Search as SearchIcon,
  LocationOn as LocationIcon,
  Star as StarIcon,
  AccessTime as TimeIcon,
  Storefront as StorefrontIcon,
  Restaurant as RestaurantIcon,
  LocalCafe as CafeIcon,
  LocalOffer as OfferIcon,
  FilterList as FilterIcon,
  Close as CloseIcon,
  TuneRounded as TuneIcon,
  VerifiedRounded as VerifiedIcon,
  TrendingUp as TrendingIcon,
  NearMe as NearMeIcon,
  GridView as GridIcon,
  ViewList as ListIcon,
  FavoriteBorder as FavoriteIcon,
  Favorite as FavoriteFilledIcon,
  DirectionsWalk as WalkIcon,
  LocalShipping as DeliveryIcon,
  Clear as ClearIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import api from '../services/api';
import noPhoto from '../assets/no_photo.webp';
import currencyTrans from '../components/CarrancyTrans';

/* ============================================================
   Country config — easy to extend
   ============================================================ */
const COUNTRIES = [
  { code: 'SY', name_en: 'Syria', name_ar: 'سوريا', flag: '🇸🇾', dial: '+963' },
  { code: 'LB', name_en: 'Lebanon', name_ar: 'لبنان', flag: '🇱🇧', dial: '+961' },
  { code: 'JO', name_en: 'Jordan', name_ar: 'الأردن', flag: '🇯🇴', dial: '+962' },
  { code: 'IQ', name_en: 'Iraq', name_ar: 'العراق', flag: '🇮🇶', dial: '+964' },
  { code: 'SA', name_en: 'Saudi Arabia', name_ar: 'السعودية', flag: '🇸🇦', dial: '+966' },
  { code: 'AE', name_en: 'UAE', name_ar: 'الإمارات', flag: '🇦🇪', dial: '+971' },
  { code: 'EG', name_en: 'Egypt', name_ar: 'مصر', flag: '🇪🇬', dial: '+20' },
  { code: 'TR', name_en: 'Turkey', name_ar: 'تركيا', flag: '🇹🇷', dial: '+90' },
  { code: 'MY', name_en: 'Malaysia', name_ar: 'ماليزيا', flag: '🇲🇾', dial: '+60' },
];

const DEFAULT_COUNTRY = 'SY';

const PLACE_TYPES = [
  { value: 'all', label_en: 'All', label_ar: 'الكل', icon: <StorefrontIcon /> },
  { value: 'Restaurant', label_en: 'Restaurants', label_ar: 'مطاعم', icon: <RestaurantIcon /> },
  { value: 'Cafe', label_en: 'Cafes', label_ar: 'مقاهي', icon: <CafeIcon /> },
  { value: 'Super market', label_en: 'Stores', label_ar: 'متاجر', icon: <StorefrontIcon /> },
];

const SORT_OPTIONS = [
  { value: 'featured', label_en: 'Featured', label_ar: 'المميزة' },
  { value: 'rating', label_en: 'Top rated', label_ar: 'الأعلى تقييماً' },
  { value: 'newest', label_en: 'Newest', label_ar: 'الأحدث' },
  { value: 'distance', label_en: 'Nearest', label_ar: 'الأقرب' },
];

const PRICE_RANGES = [
  { value: 'all', label_en: 'Any price', label_ar: 'أي سعر' },
  { value: '1', label_en: '$', label_ar: '$' },
  { value: '2', label_en: '$$', label_ar: '$$' },
  { value: '3', label_en: '$$$', label_ar: '$$$' },
  { value: '4', label_en: '$$$$', label_ar: '$$$$' },
];

/* ============================================================
   TourGuidePage
   ============================================================ */
const TourGuidePage = ({ keywordsData, language }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  const isRtl = language === 'ar';
  const [searchParams, setSearchParams] = useSearchParams();

  // Design tokens
  const goldAccent = '#E6B800';
  const goldBright = '#FFD700';
  const softGold = 'rgba(230, 184, 0, 0.12)';
  const surface = 'rgba(20, 20, 20, 0.75)';
  const cardBg = 'rgba(255, 255, 255, 0.025)';
  const border = 'rgba(230, 184, 0, 0.15)';

  // ---------- State ----------
  const [country, setCountry] = useState(searchParams.get('country') || DEFAULT_COUNTRY);
  const [placeType, setPlaceType] = useState(searchParams.get('type') || 'all');
  const [query, setQuery] = useState(searchParams.get('q') || '');
  const [city, setCity] = useState(searchParams.get('city') || '');
  const [sortBy, setSortBy] = useState('featured');
  const [priceRange, setPriceRange] = useState('all');
  const [ratingMin, setRatingMin] = useState(0);
  const [openNow, setOpenNow] = useState(false);
  const [hasDelivery, setHasDelivery] = useState(false);
  const [onlyVerified, setOnlyVerified] = useState(false);
  const [viewMode, setViewMode] = useState('grid');
  const [page, setPage] = useState(1);
  const [perPage] = useState(12);

  const [places, setPlaces] = useState([]);
  const [cities, setCities] = useState([]); // cities for the selected country
  const [loading, setLoading] = useState(true);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [favorites, setFavorites] = useState([]);
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  const activeFiltersCount = useMemo(() => {
    let n = 0;
    if (placeType !== 'all') n++;
    if (city) n++;
    if (priceRange !== 'all') n++;
    if (ratingMin > 0) n++;
    if (openNow) n++;
    if (hasDelivery) n++;
    if (onlyVerified) n++;
    return n;
  }, [placeType, city, priceRange, ratingMin, openNow, hasDelivery, onlyVerified]);

  // ---------- Load favorites from localStorage ----------
  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('tour_favorites') || '[]');
      setFavorites(saved);
    } catch {}
  }, []);

  const toggleFavorite = (id, e) => {
    e?.stopPropagation();
    setFavorites((prev) => {
      const next = prev.includes(id) ? prev.filter((f) => f !== id) : [...prev, id];
      localStorage.setItem('tour_favorites', JSON.stringify(next));
      return next;
    });
  };

  // ---------- Fetch cities when country changes ----------
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/locations/cities', { params: { country } });
        if (!cancelled) setCities(res.data?.cities || []);
      } catch (err) {
        console.warn('Cities fetch failed:', err);
        // fallback: derive from places on next fetch
        if (!cancelled) setCities([]);
      }
    })();
    return () => { cancelled = true; };
  }, [country]);

  // ---------- Fetch places ----------
  const fetchPlaces = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        country,
        type: placeType !== 'all' ? placeType : undefined,
        q: query || undefined,
        city: city || undefined,
        sort: sortBy,
        price: priceRange !== 'all' ? priceRange : undefined,
        rating_min: ratingMin > 0 ? ratingMin : undefined,
        open_now: openNow || undefined,
        delivery: hasDelivery || undefined,
        verified: onlyVerified || undefined,
        page,
        limit: perPage,
      };
      const res = await api.get('/tour/places', { params });
      const data = res.data || {};
      setPlaces(data.places || data.results || []);
      setTotalPages(data.totalPages || data.total_pages || 1);
      setTotalCount(data.total || data.count || (data.places?.length || 0));

      // Derive cities if API didn't return them
      if (!cities.length && data.places?.length) {
        const uniq = [...new Set(data.places.map((p) => p.city).filter(Boolean))];
        setCities(uniq);
      }
    } catch (err) {
      console.error('Places fetch failed:', err);
      setPlaces([]);
      setTotalPages(1);
      setTotalCount(0);
      setSnackbar({
        open: true,
        message: isRtl ? 'تعذّر تحميل النتائج' : 'Failed to load results',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, [
    country, placeType, query, city, sortBy, priceRange, ratingMin,
    openNow, hasDelivery, onlyVerified, page, perPage, cities.length, isRtl,
  ]);

  useEffect(() => {
    fetchPlaces();
  }, [fetchPlaces]);

  // ---------- Sync URL params ----------
  useEffect(() => {
    const params = new URLSearchParams();
    if (country && country !== DEFAULT_COUNTRY) params.set('country', country);
    if (placeType !== 'all') params.set('type', placeType);
    if (query) params.set('q', query);
    if (city) params.set('city', city);
    setSearchParams(params, { replace: true });
  }, [country, placeType, query, city, setSearchParams]);

  // ---------- Reset page when filters change ----------
  useEffect(() => { setPage(1); }, [
    country, placeType, query, city, sortBy, priceRange, ratingMin, openNow, hasDelivery, onlyVerified,
  ]);

  // ---------- Reset all filters ----------
  const clearAllFilters = () => {
    setPlaceType('all');
    setCity('');
    setPriceRange('all');
    setRatingMin(0);
    setOpenNow(false);
    setHasDelivery(false);
    setOnlyVerified(false);
    setSortBy('featured');
    setPage(1);
  };

  // ---------- Current country object ----------
  const currentCountry = useMemo(
    () => COUNTRIES.find((c) => c.code === country) || COUNTRIES[0],
    [country]
  );

  // ---------- SEO ----------
  const seoTitle = isRtl
    ? `دليل المطاعم والمقاهي والمتاجر في ${currentCountry.name_ar} | QR Table`
    : `Restaurants, Cafes & Stores in ${currentCountry.name_en} | QR Table`;
  const seoDescription = isRtl
    ? `اكتشف أفضل المطاعم والمقاهي والمتاجر في ${currentCountry.name_ar}. فلاتر ذكية، تقييمات، ومواقع مباشرة على QR Table.`
    : `Discover the best restaurants, cafes, and stores in ${currentCountry.name_en}. Smart filters, ratings, and live locations on QR Table.`;

  return (
    <>
      <Helmet htmlAttributes={{ lang: isRtl ? 'ar' : 'en', dir: isRtl ? 'rtl' : 'ltr' }}>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDescription} />
        <link rel="canonical" href={typeof window !== 'undefined' ? window.location.href : ''} />
      </Helmet>

      <Box
        dir={isRtl ? 'rtl' : 'ltr'}
        sx={{
          bgcolor: '#0A0A0A',
          minHeight: '100vh',
          pb: { xs: 12, md: 14 },
          background: `
            radial-gradient(circle at 10% 0%, rgba(230,184,0,0.05) 0%, transparent 45%),
            radial-gradient(circle at 90% 100%, rgba(230,184,0,0.03) 0%, transparent 45%),
            #0A0A0A
          `,
        }}
      >
        {/* ============ HEADER / HERO ============ */}
        <Box
          sx={{
            pt: { xs: 4, sm: 5, md: 7 },
            pb: { xs: 3, sm: 4 },
            borderBottom: `1px solid rgba(255,255,255,0.05)`,
          }}
        >
          <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
            <Box sx={{ textAlign: 'center', mb: { xs: 3, md: 4 } }}>
              <Chip
                label={isRtl ? '🗺️ دليل QR Table' : '🗺️ QR Table Guide'}
                sx={{
                  bgcolor: softGold,
                  color: goldBright,
                  fontWeight: 700,
                  mb: 2,
                  px: 2,
                  border: `1px solid ${border}`,
                }}
              />
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 900,
                  background: 'linear-gradient(135deg, #FFFFFF, #FFD700)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  fontSize: { xs: '1.6rem', sm: '2.2rem', md: '2.8rem' },
                  letterSpacing: -0.5,
                  mb: 1.5,
                }}
              >
                {isRtl
                  ? `استكشف ${currentCountry.name_ar}`
                  : `Explore ${currentCountry.name_en}`}
              </Typography>
              <Typography
                sx={{
                  color: '#A8A8A8',
                  fontSize: { xs: '0.85rem', sm: '0.95rem', md: '1rem' },
                  maxWidth: 620,
                  mx: 'auto',
                  px: 2,
                  lineHeight: 1.7,
                }}
              >
                {isRtl
                  ? 'دليل شامل لأفضل المطاعم والمقاهي والمتاجر — تقييمات، فلاتر ذكية، ومواقع مباشرة.'
                  : 'A curated guide to the best restaurants, cafes, and stores — ratings, smart filters, and live locations.'}
              </Typography>
            </Box>

            {/* Country picker row */}
            <Box
              sx={{
                display: 'flex',
                gap: 1,
                overflowX: 'auto',
                pb: 1.5,
                mb: 2,
                scrollbarWidth: 'none',
                '&::-webkit-scrollbar': { display: 'none' },
                justifyContent: { xs: 'flex-start', md: 'center' },
              }}
            >
              {COUNTRIES.map((c) => {
                const active = c.code === country;
                return (
                  <Chip
                    key={c.code}
                    onClick={() => setCountry(c.code)}
                    avatar={<Avatar sx={{ bgcolor: 'transparent !important', fontSize: 18 }}>{c.flag}</Avatar>}
                    label={
                      <Typography
                        component="span"
                        sx={{
                          fontWeight: active ? 800 : 600,
                          fontSize: '0.78rem',
                          color: active ? '#111' : '#D0D0D0',
                          ml: isRtl ? 0.5 : 0,
                          mr: isRtl ? 0 : 0.5,
                        }}
                      >
                        {isRtl ? c.name_ar : c.name_en}
                      </Typography>
                    }
                    sx={{
                      flexShrink: 0,
                      height: 38,
                      px: 0.5,
                      borderRadius: '999px',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      background: active
                        ? `linear-gradient(135deg, ${goldAccent}, ${goldBright})`
                        : 'rgba(255,255,255,0.04)',
                      border: `1px solid ${active ? goldBright : 'rgba(255,255,255,0.08)'}`,
                      boxShadow: active ? '0 6px 20px rgba(230,184,0,0.3)' : 'none',
                      '&:hover': {
                        background: active
                          ? `linear-gradient(135deg, ${goldBright}, ${goldAccent})`
                          : 'rgba(230,184,0,0.1)',
                        borderColor: active ? goldBright : border,
                      },
                    }}
                  />
                );
              })}
            </Box>

            {/* Search + type pills */}
            <Box
              sx={{
                display: 'flex',
                gap: 1.5,
                flexDirection: { xs: 'column', md: 'row' },
                alignItems: { xs: 'stretch', md: 'center' },
                mb: 2.5,
              }}
            >
              <TextField
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={
                  isRtl ? 'ابحث باسم المكان أو الحي…' : 'Search by name or neighborhood…'
                }
                fullWidth
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: '#7A7A7A', fontSize: 20 }} />
                    </InputAdornment>
                  ),
                  endAdornment: query && (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => setQuery('')}
                        sx={{ color: '#7A7A7A' }}
                      >
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  maxWidth: { md: 480 },
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '999px',
                    bgcolor: 'rgba(255,255,255,0.04)',
                    '& fieldset': { borderColor: 'rgba(255,255,255,0.08)' },
                    '&:hover fieldset': { borderColor: border },
                    '&.Mui-focused fieldset': { borderColor: goldBright },
                  },
                  '& input': { color: '#fff', fontSize: '0.88rem' },
                }}
              />

              <Box
                sx={{
                  display: 'flex',
                  gap: 1,
                  overflowX: 'auto',
                  pb: { xs: 0.5, md: 0 },
                  scrollbarWidth: 'none',
                  '&::-webkit-scrollbar': { display: 'none' },
                }}
              >
                {PLACE_TYPES.map((t) => {
                  const active = placeType === t.value;
                  return (
                    <Button
                      key={t.value}
                      onClick={() => setPlaceType(t.value)}
                      startIcon={t.icon}
                      sx={{
                        flexShrink: 0,
                        borderRadius: '999px',
                        px: 2,
                        py: 0.9,
                        minWidth: 'auto',
                        fontSize: '0.8rem',
                        fontWeight: 700,
                        textTransform: 'none',
                        color: active ? '#111' : '#D0D0D0',
                        background: active
                          ? `linear-gradient(135deg, ${goldAccent}, ${goldBright})`
                          : 'rgba(255,255,255,0.04)',
                        border: `1px solid ${active ? goldBright : 'rgba(255,255,255,0.08)'}`,
                        boxShadow: active ? '0 6px 20px rgba(230,184,0,0.3)' : 'none',
                        '& .MuiButton-startIcon': {
                          mr: { xs: 0.5, sm: 1 },
                          '& svg': { fontSize: 18 },
                        },
                        '&:hover': {
                          background: active
                            ? `linear-gradient(135deg, ${goldBright}, ${goldAccent})`
                            : 'rgba(230,184,0,0.1)',
                        },
                      }}
                    >
                      {isMobile ? '' : isRtl ? t.label_ar : t.label_en}
                    </Button>
                  );
                })}
              </Box>

              {/* Filter button (mobile) */}
              <Badge
                badgeContent={activeFiltersCount}
                color="warning"
                sx={{
                  display: { xs: 'inline-flex', md: 'none' },
                  '& .MuiBadge-badge': {
                    bgcolor: goldBright,
                    color: '#111',
                    fontWeight: 800,
                  },
                }}
              >
                <Button
                  onClick={() => setFilterDrawerOpen(true)}
                  startIcon={<TuneIcon />}
                  sx={{
                    borderRadius: '999px',
                    px: 2.5,
                    py: 1,
                    textTransform: 'none',
                    fontWeight: 700,
                    color: '#fff',
                    border: `1px solid ${border}`,
                    background: 'rgba(255,255,255,0.03)',
                    '&:hover': { background: softGold },
                  }}
                >
                  {isRtl ? 'فلترة' : 'Filters'}
                </Button>
              </Badge>
            </Box>

            {/* Sub-filters row (desktop) */}
            {!isMobile && (
              <Box
                sx={{
                  display: 'flex',
                  gap: 1.5,
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  justifyContent: 'space-between',
                  mb: 2,
                }}
              >
                <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
                  {/* City */}
                  <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel sx={{ color: '#8A8A8A' }}>
                      {isRtl ? 'المدينة' : 'City'}
                    </InputLabel>
                    <Select
                      value={city}
                      onChange={(e) => setCity(e.target.value)}
                      label={isRtl ? 'المدينة' : 'City'}
                      sx={{
                        color: '#fff',
                        bgcolor: 'rgba(255,255,255,0.03)',
                        borderRadius: '999px',
                        '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.08)' },
                        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: border },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: goldBright },
                        '& .MuiSvgIcon-root': { color: '#B8B8B8' },
                        '& .MuiSelect-select': { fontSize: '0.85rem', py: 1 },
                      }}
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            bgcolor: '#141414',
                            border: `1px solid ${border}`,
                            '& .MuiMenuItem-root': {
                              color: '#fff',
                              fontSize: '0.85rem',
                              '&:hover': { bgcolor: softGold },
                              '&.Mui-selected': { bgcolor: 'rgba(230,184,0,0.15) !important' },
                            },
                          },
                        },
                      }}
                    >
                      <MenuItem value="">{isRtl ? 'كل المدن' : 'All cities'}</MenuItem>
                      {cities.map((c) => (
                        <MenuItem key={c} value={c}>
                          {c}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {/* Price */}
                  <FormControl size="small" sx={{ minWidth: 130 }}>
                    <InputLabel sx={{ color: '#8A8A8A' }}>
                      {isRtl ? 'السعر' : 'Price'}
                    </InputLabel>
                    <Select
                      value={priceRange}
                      onChange={(e) => setPriceRange(e.target.value)}
                      label={isRtl ? 'السعر' : 'Price'}
                      sx={{
                        color: '#fff',
                        bgcolor: 'rgba(255,255,255,0.03)',
                        borderRadius: '999px',
                        '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.08)' },
                        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: border },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: goldBright },
                        '& .MuiSvgIcon-root': { color: '#B8B8B8' },
                        '& .MuiSelect-select': { fontSize: '0.85rem', py: 1 },
                      }}
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            bgcolor: '#141414',
                            border: `1px solid ${border}`,
                            '& .MuiMenuItem-root': {
                              color: '#fff',
                              fontSize: '0.85rem',
                              '&:hover': { bgcolor: softGold },
                            },
                          },
                        },
                      }}
                    >
                      {PRICE_RANGES.map((p) => (
                        <MenuItem key={p.value} value={p.value}>
                          {isRtl ? p.label_ar : p.label_en}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {/* Sort */}
                  <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel sx={{ color: '#8A8A8A' }}>
                      {isRtl ? 'الترتيب' : 'Sort by'}
                    </InputLabel>
                    <Select
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                      label={isRtl ? 'الترتيب' : 'Sort by'}
                      sx={{
                        color: '#fff',
                        bgcolor: 'rgba(255,255,255,0.03)',
                        borderRadius: '999px',
                        '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.08)' },
                        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: border },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: goldBright },
                        '& .MuiSvgIcon-root': { color: '#B8B8B8' },
                        '& .MuiSelect-select': { fontSize: '0.85rem', py: 1 },
                      }}
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            bgcolor: '#141414',
                            border: `1px solid ${border}`,
                            '& .MuiMenuItem-root': {
                              color: '#fff',
                              fontSize: '0.85rem',
                              '&:hover': { bgcolor: softGold },
                            },
                          },
                        },
                      }}
                    >
                      {SORT_OPTIONS.map((s) => (
                        <MenuItem key={s.value} value={s.value}>
                          {isRtl ? s.label_ar : s.label_en}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {/* Quick toggles */}
                  <Chip
                    icon={<TimeIcon sx={{ fontSize: '1rem !important' }} />}
                    label={isRtl ? 'مفتوح الآن' : 'Open now'}
                    onClick={() => setOpenNow((v) => !v)}
                    sx={{
                      cursor: 'pointer',
                      color: openNow ? '#111' : '#D0D0D0',
                      bgcolor: openNow ? goldBright : 'rgba(255,255,255,0.04)',
                      border: `1px solid ${openNow ? goldBright : 'rgba(255,255,255,0.08)'}`,
                      fontWeight: 700,
                      fontSize: '0.78rem',
                      '& .MuiChip-icon': { color: openNow ? '#111' : '#B8B8B8' },
                    }}
                  />
                  <Chip
                    icon={<DeliveryIcon sx={{ fontSize: '1rem !important' }} />}
                    label={isRtl ? 'توصيل' : 'Delivery'}
                    onClick={() => setHasDelivery((v) => !v)}
                    sx={{
                      cursor: 'pointer',
                      color: hasDelivery ? '#111' : '#D0D0D0',
                      bgcolor: hasDelivery ? goldBright : 'rgba(255,255,255,0.04)',
                      border: `1px solid ${hasDelivery ? goldBright : 'rgba(255,255,255,0.08)'}`,
                      fontWeight: 700,
                      fontSize: '0.78rem',
                      '& .MuiChip-icon': { color: hasDelivery ? '#111' : '#B8B8B8' },
                    }}
                  />
                  <Chip
                    icon={<VerifiedIcon sx={{ fontSize: '1rem !important' }} />}
                    label={isRtl ? 'موثّق' : 'Verified'}
                    onClick={() => setOnlyVerified((v) => !v)}
                    sx={{
                      cursor: 'pointer',
                      color: onlyVerified ? '#111' : '#D0D0D0',
                      bgcolor: onlyVerified ? goldBright : 'rgba(255,255,255,0.04)',
                      border: `1px solid ${onlyVerified ? goldBright : 'rgba(255,255,255,0.08)'}`,
                      fontWeight: 700,
                      fontSize: '0.78rem',
                      '& .MuiChip-icon': { color: onlyVerified ? '#111' : '#B8B8B8' },
                    }}
                  />
                </Stack>

                <Stack direction="row" spacing={0.5} alignItems="center">
                  {/* View toggle */}
                  <IconButton
                    onClick={() => setViewMode('grid')}
                    size="small"
                    sx={{
                      borderRadius: '10px',
                      color: viewMode === 'grid' ? goldBright : '#7A7A7A',
                      bgcolor: viewMode === 'grid' ? softGold : 'transparent',
                    }}
                  >
                    <GridIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    onClick={() => setViewMode('list')}
                    size="small"
                    sx={{
                      borderRadius: '10px',
                      color: viewMode === 'list' ? goldBright : '#7A7A7A',
                      bgcolor: viewMode === 'list' ? softGold : 'transparent',
                    }}
                  >
                    <ListIcon fontSize="small" />
                  </IconButton>
                  {activeFiltersCount > 0 && (
                    <Button
                      size="small"
                      onClick={clearAllFilters}
                      startIcon={<ClearIcon />}
                      sx={{
                        color: '#FF8A80',
                        textTransform: 'none',
                        fontSize: '0.75rem',
                        '&:hover': { bgcolor: 'rgba(255,82,82,0.1)' },
                      }}
                    >
                      {isRtl ? 'مسح الفلاتر' : 'Clear'}
                    </Button>
                  )}
                </Stack>
              </Box>
            )}

            {/* Results count */}
            <Typography
              sx={{
                color: '#8A8A8A',
                fontSize: '0.8rem',
                mt: 1,
                textAlign: isRtl ? 'right' : 'left',
              }}
            >
              {loading
                ? isRtl
                  ? 'جار التحميل…'
                  : 'Loading…'
                : isRtl
                ? `${totalCount} نتيجة في ${currentCountry.name_ar}`
                : `${totalCount} results in ${currentCountry.name_en}`}
            </Typography>
          </Container>
        </Box>

        {/* ============ GRID / LIST ============ */}
        <Container maxWidth="lg" sx={{ py: { xs: 3, sm: 4 }, px: { xs: 2, sm: 3, md: 4 } }}>
          {loading ? (
            <Grid container spacing={{ xs: 2, sm: 2.5 }}>
              {Array.from({ length: 6 }).map((_, i) => (
                <Grid item xs={12} sm={6} md={4} key={i}>
                  <Skeleton
                    variant="rectangular"
                    height={320}
                    sx={{ bgcolor: 'rgba(255,255,255,0.03)', borderRadius: '20px' }}
                  />
                </Grid>
              ))}
            </Grid>
          ) : places.length === 0 ? (
            <Paper
              elevation={0}
              sx={{
                p: { xs: 4, sm: 6 },
                textAlign: 'center',
                bgcolor: cardBg,
                border: `1px dashed rgba(255,255,255,0.08)`,
                borderRadius: '20px',
              }}
            >
              <Box
                sx={{
                  width: 72,
                  height: 72,
                  borderRadius: '50%',
                  bgcolor: softGold,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 2,
                }}
              >
                <SearchIcon sx={{ fontSize: 36, color: goldBright }} />
              </Box>
              <Typography variant="h6" sx={{ color: '#fff', fontWeight: 700, mb: 1 }}>
                {isRtl ? 'لا توجد نتائج' : 'No results found'}
              </Typography>
              <Typography sx={{ color: '#8A8A8A', fontSize: '0.85rem', mb: 2 }}>
                {isRtl
                  ? 'جرّب تغيير الفلاتر أو البحث في مدينة أخرى.'
                  : 'Try changing filters or searching in another city.'}
              </Typography>
              <Button
                onClick={clearAllFilters}
                startIcon={<ClearIcon />}
                sx={{
                  borderRadius: '999px',
                  textTransform: 'none',
                  background: `linear-gradient(135deg, ${goldAccent}, ${goldBright})`,
                  color: '#111',
                  fontWeight: 800,
                  px: 3,
                  py: 1.2,
                  '&:hover': { transform: 'translateY(-1px)' },
                }}
              >
                {isRtl ? 'مسح الفلاتر' : 'Clear filters'}
              </Button>
            </Paper>
          ) : (
            <Grid container spacing={{ xs: 2, sm: 2.5 }}>
              <AnimatePresence mode="popLayout">
                {places.map((place, i) => (
                  <Grid
                    item
                    xs={12}
                    sm={viewMode === 'list' ? 12 : 6}
                    md={viewMode === 'list' ? 12 : 4}
                    key={place.id}
                  >
                    <motion.div
                      layout
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ duration: 0.3, delay: i * 0.03 }}
                      style={{ height: '100%' }}
                    >
                      <PlaceCard
                        place={place}
                        language={language}
                        isRtl={isRtl}
                        isMobile={isMobile}
                        viewMode={viewMode}
                        isFavorite={favorites.includes(place.id)}
                        onToggleFavorite={(e) => toggleFavorite(place.id, e)}
                        onClick={() => navigate(`/tour/places/${place.id}`)}
                        goldAccent={goldAccent}
                        goldBright={goldBright}
                        cardBg={cardBg}
                        border={border}
                      />
                    </motion.div>
                  </Grid>
                ))}
              </AnimatePresence>
            </Grid>
          )}

          {/* Pagination */}
          {!loading && totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 5 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(_, v) => {
                  setPage(v);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                siblingCount={isMobile ? 0 : 1}
                boundaryCount={1}
                sx={{
                  '& .MuiPaginationItem-root': {
                    color: '#B8B8B8',
                    border: '1px solid rgba(255,255,255,0.06)',
                    borderRadius: '10px',
                    fontSize: '0.85rem',
                    '&:hover': { bgcolor: softGold, color: goldBright },
                    '&.Mui-selected': {
                      background: `linear-gradient(135deg, ${goldAccent}, ${goldBright})`,
                      color: '#111',
                      fontWeight: 800,
                      border: `1px solid ${goldBright}`,
                      '&:hover': { background: `linear-gradient(135deg, ${goldBright}, ${goldAccent})` },
                    },
                  },
                }}
              />
            </Box>
          )}
        </Container>
      </Box>

      {/* ============ FILTER DRAWER (mobile) ============ */}
      <Drawer
        anchor="bottom"
        open={filterDrawerOpen}
        onClose={() => setFilterDrawerOpen(false)}
        PaperProps={{
          sx: {
            bgcolor: 'rgba(20, 20, 20, 0.97)',
            backdropFilter: 'blur(24px)',
            WebkitBackdropFilter: 'blur(24px)',
            borderTopLeftRadius: 24,
            borderTopRightRadius: 24,
            borderTop: `1px solid ${border}`,
            color: '#fff',
            maxHeight: '85vh',
          },
        }}
      >
        <Box sx={{ p: 3, pt: 2, dir: isRtl ? 'rtl' : 'ltr' }}>
          {/* Grabber */}
          <Box
            sx={{
              width: 40,
              height: 4,
              borderRadius: 2,
              bgcolor: 'rgba(255,255,255,0.15)',
              mx: 'auto',
              mb: 2,
            }}
          />

          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography sx={{ fontWeight: 800, fontSize: '1.05rem' }}>
              {isRtl ? 'تصفية النتائج' : 'Filter results'}
            </Typography>
            <IconButton onClick={() => setFilterDrawerOpen(false)} sx={{ color: '#9A9A9A' }}>
              <CloseIcon />
            </IconButton>
          </Box>

          <Stack spacing={2.5} sx={{ overflowY: 'auto', maxHeight: '60vh', pb: 1 }}>
            {/* City */}
            <Box>
              <Typography sx={{ fontSize: '0.8rem', color: '#B8B8B8', fontWeight: 700, mb: 1 }}>
                {isRtl ? 'المدينة' : 'City'}
              </Typography>
              <Select
                fullWidth
                size="small"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                displayEmpty
                sx={{
                  color: '#fff',
                  bgcolor: 'rgba(255,255,255,0.03)',
                  borderRadius: '12px',
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.08)' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: border },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: goldBright },
                  '& .MuiSvgIcon-root': { color: '#B8B8B8' },
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      bgcolor: '#141414',
                      border: `1px solid ${border}`,
                      '& .MuiMenuItem-root': {
                        color: '#fff',
                        '&:hover': { bgcolor: softGold },
                      },
                    },
                  },
                }}
              >
                <MenuItem value="">{isRtl ? 'كل المدن' : 'All cities'}</MenuItem>
                {cities.map((c) => (
                  <MenuItem key={c} value={c}>{c}</MenuItem>
                ))}
              </Select>
            </Box>

            {/* Sort */}
            <Box>
              <Typography sx={{ fontSize: '0.8rem', color: '#B8B8B8', fontWeight: 700, mb: 1 }}>
                {isRtl ? 'الترتيب' : 'Sort by'}
              </Typography>
              <Select
                fullWidth
                size="small"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                sx={{
                  color: '#fff',
                  bgcolor: 'rgba(255,255,255,0.03)',
                  borderRadius: '12px',
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.08)' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: border },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: goldBright },
                  '& .MuiSvgIcon-root': { color: '#B8B8B8' },
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      bgcolor: '#141414',
                      border: `1px solid ${border}`,
                      '& .MuiMenuItem-root': {
                        color: '#fff',
                        '&:hover': { bgcolor: softGold },
                      },
                    },
                  },
                }}
              >
                {SORT_OPTIONS.map((s) => (
                  <MenuItem key={s.value} value={s.value}>
                    {isRtl ? s.label_ar : s.label_en}
                  </MenuItem>
                ))}
              </Select>
            </Box>

            {/* Price */}
            <Box>
              <Typography sx={{ fontSize: '0.8rem', color: '#B8B8B8', fontWeight: 700, mb: 1 }}>
                {isRtl ? 'السعر' : 'Price'}
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                {PRICE_RANGES.map((p) => {
                  const active = priceRange === p.value;
                  return (
                    <Chip
                      key={p.value}
                      label={isRtl ? p.label_ar : p.label_en}
                      onClick={() => setPriceRange(p.value)}
                      sx={{
                        cursor: 'pointer',
                        color: active ? '#111' : '#D0D0D0',
                        bgcolor: active ? goldBright : 'rgba(255,255,255,0.04)',
                        border: `1px solid ${active ? goldBright : 'rgba(255,255,255,0.08)'}`,
                        fontWeight: 700,
                      }}
                    />
                  );
                })}
              </Stack>
            </Box>

            {/* Rating */}
            <Box>
              <Typography sx={{ fontSize: '0.8rem', color: '#B8B8B8', fontWeight: 700, mb: 1 }}>
                {isRtl ? 'التقييم الأدنى' : 'Minimum rating'}
              </Typography>
              <Box sx={{ px: 1 }}>
                <Slider
                  value={ratingMin}
                  onChange={(_, v) => setRatingMin(v)}
                  min={0}
                  max={5}
                  step={0.5}
                  marks={[
                    { value: 0, label: '0' },
                    { value: 2.5, label: '2.5' },
                    { value: 5, label: '5' },
                  ]}
                  sx={{
                    color: goldBright,
                    '& .MuiSlider-markLabel': { color: '#8A8A8A', fontSize: '0.7rem' },
                  }}
                />
                <Typography sx={{ fontSize: '0.8rem', color: goldBright, fontWeight: 700, textAlign: 'center' }}>
                  {ratingMin}+ ⭐
                </Typography>
              </Box>
            </Box>

            {/* Toggles */}
            <Stack spacing={1}>
              <FormControlLabel
                control={
                  <Switch
                    checked={openNow}
                    onChange={(e) => setOpenNow(e.target.checked)}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': { color: goldBright },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        bgcolor: goldAccent,
                        opacity: 0.7,
                      },
                      '& .MuiSwitch-track': { bgcolor: '#3A3A3A' },
                    }}
                  />
                }
                label={<Typography sx={{ fontSize: '0.88rem' }}>{isRtl ? 'مفتوح الآن' : 'Open now'}</Typography>}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={hasDelivery}
                    onChange={(e) => setHasDelivery(e.target.checked)}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': { color: goldBright },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        bgcolor: goldAccent,
                        opacity: 0.7,
                      },
                      '& .MuiSwitch-track': { bgcolor: '#3A3A3A' },
                    }}
                  />
                }
                label={<Typography sx={{ fontSize: '0.88rem' }}>{isRtl ? 'توصيل متاح' : 'Delivery available'}</Typography>}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={onlyVerified}
                    onChange={(e) => setOnlyVerified(e.target.checked)}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': { color: goldBright },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        bgcolor: goldAccent,
                        opacity: 0.7,
                      },
                      '& .MuiSwitch-track': { bgcolor: '#3A3A3A' },
                    }}
                  />
                }
                label={<Typography sx={{ fontSize: '0.88rem' }}>{isRtl ? 'الحسابات الموثّقة فقط' : 'Verified only'}</Typography>}
              />
            </Stack>
          </Stack>

          {/* Actions */}
          <Stack direction="row" spacing={1.5} sx={{ mt: 3 }}>
            <Button
              fullWidth
              onClick={clearAllFilters}
              sx={{
                color: '#B8B8B8',
                border: `1px solid rgba(255,255,255,0.08)`,
                borderRadius: '999px',
                py: 1.3,
                textTransform: 'none',
                fontWeight: 700,
                '&:hover': { bgcolor: 'rgba(255,255,255,0.03)' },
              }}
            >
              {isRtl ? 'مسح' : 'Clear'}
            </Button>
            <Button
              fullWidth
              onClick={() => setFilterDrawerOpen(false)}
              sx={{
                background: `linear-gradient(135deg, ${goldAccent}, ${goldBright})`,
                color: '#111',
                borderRadius: '999px',
                py: 1.3,
                textTransform: 'none',
                fontWeight: 800,
                boxShadow: '0 8px 24px rgba(230,184,0,0.28)',
                '&:hover': { transform: 'translateY(-1px)' },
              }}
            >
              {isRtl ? 'عرض النتائج' : 'Show results'}
            </Button>
          </Stack>
        </Box>
      </Drawer>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          sx={{
            bgcolor: surface,
            color: '#fff',
            border: `1px solid ${border}`,
            borderRadius: '14px',
            fontWeight: 600,
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

/* ============================================================
   PlaceCard
   ============================================================ */
const PlaceCard = ({
  place,
  language,
  isRtl,
  isMobile,
  viewMode,
  isFavorite,
  onToggleFavorite,
  onClick,
  goldAccent,
  goldBright,
  cardBg,
  border,
}) => {
  const [imageError, setImageError] = React.useState(false);
  const [imageLoaded, setImageLoaded] = React.useState(false);

  const isList = viewMode === 'list';

  // Place type icon
  const typeIcon = (() => {
    switch (place.type) {
      case 'Restaurant': return <RestaurantIcon sx={{ fontSize: 16 }} />;
      case 'Cafe': return <CafeIcon sx={{ fontSize: 16 }} />;
      case 'Super market': return <StorefrontIcon sx={{ fontSize: 16 }} />;
      default: return <StorefrontIcon sx={{ fontSize: 16 }} />;
    }
  })();

  const typeLabel = (() => {
    const t = place.type;
    if (isRtl) {
      return t === 'Restaurant' ? 'مطعم' : t === 'Cafe' ? 'مقهى' : 'متجر';
    }
    return t === 'Restaurant' ? 'Restaurant' : t === 'Cafe' ? 'Cafe' : 'Store';
  })();

  return (
    <Card
      onClick={onClick}
      sx={{
        bgcolor: cardBg,
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
        border: `1px solid rgba(255,255,255,0.06)`,
        borderRadius: '20px',
        cursor: 'pointer',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        overflow: 'hidden',
        position: 'relative',
        height: '100%',
        display: 'flex',
        flexDirection: isList && !isMobile ? (isRtl ? 'row-reverse' : 'row') : 'column',
        '&:hover': {
          transform: 'translateY(-6px)',
          borderColor: border,
          boxShadow: '0 16px 40px rgba(230,184,0,0.12)',
        },
      }}
    >
      {/* Image */}
      <Box
        sx={{
          position: 'relative',
          height: isList && !isMobile ? 'auto' : { xs: 180, sm: 190 },
          width: isList && !isMobile ? { md: 280 } : '100%',
          flexShrink: 0,
          overflow: 'hidden',
        }}
      >
        {!imageLoaded && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              bgcolor: 'rgba(255,255,255,0.03)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Box
              sx={{
                width: 34,
                height: 34,
                borderRadius: '50%',
                border: '2px solid rgba(255,255,255,0.06)',
                borderTopColor: goldBright,
                animation: 'spin 0.8s linear infinite',
                '@keyframes spin': {
                  '0%': { transform: 'rotate(0deg)' },
                  '100%': { transform: 'rotate(360deg)' },
                },
              }}
            />
          </Box>
        )}

        <CardMedia
          component="img"
          image={!imageError ? place.cover_image || place.logo || noPhoto : noPhoto}
          alt={place.name}
          loading="lazy"
          onLoad={() => setImageLoaded(true)}
          onError={() => setImageError(true)}
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            transition: 'transform 0.5s ease',
            '&:hover': { transform: 'scale(1.06)' },
          }}
        />

        {/* Type badge (top-left/right) */}
        <Chip
          icon={typeIcon}
          label={typeLabel}
          size="small"
          sx={{
            position: 'absolute',
            top: 12,
            [isRtl ? 'right' : 'left']: 12,
            bgcolor: 'rgba(0,0,0,0.7)',
            color: goldBright,
            backdropFilter: 'blur(8px)',
            border: `1px solid ${border}`,
            fontWeight: 700,
            fontSize: '0.68rem',
            height: 24,
            zIndex: 3,
            '& .MuiChip-icon': { color: goldBright, ml: isRtl ? 0.5 : 0, mr: isRtl ? 0 : 0.5 },
          }}
        />

        {/* Favorite */}
        <IconButton
          size="small"
          onClick={onToggleFavorite}
          sx={{
            position: 'absolute',
            top: 12,
            [isRtl ? 'left' : 'right']: 12,
            zIndex: 3,
            width: 32,
            height: 32,
            bgcolor: 'rgba(0,0,0,0.6)',
            backdropFilter: 'blur(8px)',
            color: isFavorite ? '#FF5252' : '#fff',
            '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' },
          }}
        >
          {isFavorite ? <FavoriteFilledIcon sx={{ fontSize: 16 }} /> : <FavoriteIcon sx={{ fontSize: 16 }} />}
        </IconButton>

        {/* Verified */}
        {place.verified && (
          <Chip
            icon={<VerifiedIcon sx={{ fontSize: '1rem !important', color: '#111 !important' }} />}
            label={isRtl ? 'موثّق' : 'Verified'}
            size="small"
            sx={{
              position: 'absolute',
              bottom: 12,
              [isRtl ? 'right' : 'left']: 12,
              bgcolor: goldBright,
              color: '#111',
              fontWeight: 800,
              fontSize: '0.66rem',
              height: 22,
              zIndex: 3,
            }}
          />
        )}

        {/* Open/closed badge */}
        <Box
          sx={{
            position: 'absolute',
            bottom: 12,
            [isRtl ? 'left' : 'right']: 12,
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            bgcolor: place.is_open ? '#4CAF50' : '#FF5252',
            px: 1,
            py: 0.25,
            borderRadius: '999px',
            zIndex: 3,
          }}
        >
          <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#fff' }} />
          <Typography sx={{ color: '#fff', fontWeight: 800, fontSize: '0.65rem' }}>
            {place.is_open ? (isRtl ? 'مفتوح' : 'Open') : (isRtl ? 'مغلق' : 'Closed')}
          </Typography>
        </Box>
      </Box>

      {/* Body */}
      <CardContent
        sx={{
          p: { xs: 1.75, sm: 2 },
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
        }}
      >
        <Box>
          <Typography
            variant="h6"
            sx={{
              color: '#FFFFFF',
              fontWeight: 700,
              mb: 0.5,
              fontSize: { xs: '0.98rem', sm: '1.05rem' },
              lineHeight: 1.3,
              overflow: 'hidden',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              textAlign: isRtl ? 'right' : 'left',
            }}
          >
            {isRtl ? place.name : place.name_en || place.name}
          </Typography>

          {/* Rating + city */}
          <Stack
            direction="row"
            spacing={1.5}
            alignItems="center"
            sx={{
              mb: 1.25,
              flexDirection: isRtl ? 'row-reverse' : 'row',
              justifyContent: isRtl ? 'flex-end' : 'flex-start',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <StarIcon sx={{ fontSize: 14, color: goldBright }} />
              <Typography sx={{ color: '#D0D0D0', fontSize: '0.78rem', fontWeight: 600 }}>
                {place.rating?.toFixed(1) || '—'}
              </Typography>
              <Typography sx={{ color: '#6B6B6B', fontSize: '0.7rem' }}>
                ({place.reviews_count || 0})
              </Typography>
            </Box>
            {place.city && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                <LocationIcon sx={{ fontSize: 14, color: '#7A7A7A' }} />
                <Typography
                  sx={{
                    color: '#9A9A9A',
                    fontSize: '0.75rem',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {place.city}
                </Typography>
              </Box>
            )}
          </Stack>

          {/* Price + delivery */}
          <Stack
            direction="row"
            spacing={1.5}
            sx={{
              mb: 1,
              flexDirection: isRtl ? 'row-reverse' : 'row',
              justifyContent: isRtl ? 'flex-end' : 'flex-start',
              flexWrap: 'wrap',
            }}
          >
            {place.price_range && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                <OfferIcon sx={{ fontSize: 14, color: goldBright }} />
                <Typography sx={{ color: '#B8B8B8', fontSize: '0.72rem', fontWeight: 700 }}>
                  {'$'.repeat(place.price_range)}
                </Typography>
              </Box>
            )}

          </Stack>
        </Box>

        {/* Delivery fee / min order */}
        <Stack
          direction="row"
          spacing={1}
          sx={{
            mt: 1,
            pt: 1.25,
            borderTop: '1px solid rgba(255,255,255,0.05)',
            flexDirection: isRtl ? 'row-reverse' : 'row',
            justifyContent: 'space-between',
          }}
        >
          <Typography sx={{ color: '#7A7A7A', fontSize: '0.72rem' }}>
            {isRtl ? 'التوصيل: ' : 'Delivery: '}
            <span style={{ color: '#D0D0D0', fontWeight: 700 }}>
              {place.delivery_fee || 0} {currencyTrans(place.currency || 'SYP', language)}
            </span>
          </Typography>
          <Typography sx={{ color: '#7A7A7A', fontSize: '0.72rem' }}>
            {isRtl ? 'الحد الأدنى: ' : 'Min: '}
            <span style={{ color: '#D0D0D0', fontWeight: 700 }}>
              {place.min_order_amount || 0} {currencyTrans(place.currency || 'SYP', language)}
            </span>
          </Typography>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default TourGuidePage;